export interface ChangingCodesInput {
  filename?: string;
  sheetName?: string;
}

export interface SplittingSkillsInput {
  filename?: string;
  sheet1Name?: string;
  workflow1Uuid?: string;
  trigger1Uuid?: string;
  sheet2Name?: string;
  workflow2Uuid?: string;
  trigger2Uuid?: string;
}

export interface MergingQualificationsInput {
  filename?: string;
  sheetName?: string;
}

export interface ChangingEmployeeNumbersInput {
  filename?: string;
  sheetName?: string;
}

// Strip single quotes so the value can't break the enclosing Smalltalk string literal.
function safe(input: unknown, fallback: string): string {
  if (typeof input !== 'string') return fallback;
  const cleaned = input.trim().replace(/'/g, '');
  return cleaned.length > 0 ? cleaned : fallback;
}

export function generateChangingCodes(input: ChangingCodesInput): string {
  const filename = safe(input.filename, 'ExcelFileName.xlsx');
  const sheetName = safe(input.sheetName, 'Sheet 1');
  return `wb := Xlsx.Workbook filename: '${filename}'.
sh := wb sheetNamed: '${sheetName}'.
notFound := Core.OrderedCollection new.
sh rowsWithoutHeaderRowDo:
        [:row |
            oldCode := row trimmedValueAt: 2.
            newCode := row trimmedValueAt: 3.
            q := repository qualificationWithCode: oldCode.
            q isNil
                ifTrue: [notFound add: oldCode]
                ifFalse: [q code: newCode.]
        ].
notFound.`;
}

export function generateSplittingSkills(input: SplittingSkillsInput): string {
  const filename = safe(input.filename, 'update-prod.xlsx');
  const sheet1Name = safe(input.sheet1Name, 'Sheet 1');
  const sheet2Name = safe(input.sheet2Name, 'Sheet 2');
  const workflow1Uuid = safe(input.workflow1Uuid, 'xxx');
  const trigger1Uuid = safe(input.trigger1Uuid, 'xxx');
  const workflow2Uuid = safe(input.workflow2Uuid, 'xxx');
  const trigger2Uuid = safe(input.trigger2Uuid, 'xxx');
  return `wb := Xlsx.Workbook filename: '${filename}'.

"${sheet1Name}"
workflow1 := repository workflowAt: '${workflow1Uuid}' asUUID.
trigger1 := workflow1 triggers at: '${trigger1Uuid}' asUUID.

sh1 := wb sheetNamed: '${sheet1Name}'.

notFound1 := Core.OrderedCollection new.
found1 := Core.OrderedCollection new.
sh1 rowsWithoutHeaderRowDo:
        [:row |
            code := row trimmedValueAt: 1.
            q := repository qualificationWithCode: code asString.
            q isNil
                ifTrue: [notFound1 add: code]
                ifFalse: [found1 add: q]
        ].
trigger1 qualifications: found1.

"${sheet2Name}"
workflow2 := repository workflowAt: '${workflow2Uuid}' asUUID.
trigger2 := workflow2 triggers at: '${trigger2Uuid}' asUUID.

sh2 := wb sheetNamed: '${sheet2Name}'.

notFound2 := Core.OrderedCollection new.
found2 := Core.OrderedCollection new.
sh2 rowsWithoutHeaderRowDo:
        [:row |
            code := row trimmedValueAt: 1.
            q := repository qualificationWithCode: code asString.
            q isNil
                ifTrue: [notFound2 add: code]
                ifFalse: [found2 add: q]
        ].
trigger2 qualifications: found2.`;
}

export function generateMergingQualifications(
  input: MergingQualificationsInput,
): string {
  const filename = safe(input.filename, 'ExcelFileName.xlsx');
  const sheetName = safe(input.sheetName, 'Sheet 1');
  return `wb := Xlsx.Workbook filename: '${filename}'.
sh := wb sheetNamed: '${sheetName}'.
codes := Core.Set new.
sh rowsWithoutHeaderRowDo:
        [:row |
            codes add: (row trimmedValueAt: 2).
        ].

matches := Core.OrderedCollection new.
notFound := Core.OrderedCollection new.

codes do:
      [:code | | qs |
          qs := repository qualificationsWithCode: code.
          qs notEmpty
            ifTrue: [matches add: (SMap code: code qualifications: qs)]
            ifFalse: [notFound add: code]
        ].

size1 := ((matches select: [:m | m qualifications size = 1]) collect: [:m | m code]) asArray.
size2 := ((matches select: [:m | m qualifications size = 2]) collect: [:m | m code]) asArray.
sizeX := ((matches select: [:m | m qualifications size > 2]) collect: [:m | m code]) asArray.

size2 do:
      [:code | | qs q1 q2 |
          qs := (repository qualificationsWithCode: code) asSortedCollection: [:a :b | a id <= b id].
          q1 := qs at: 1.
          q2 := qs at: 2.
          q2 mergeInto: q1].

sizeX do:
        [:code | | qs q1 |
            qs := (repository qualificationsWithCode: code) asSortedCollection: [:a :b | a id <= b id].
            q1 := qs at: 1.
            qs allButFirst do: [:q | q mergeInto: q1]].`;
}

export function generateChangingEmployeeNumbers(
  input: ChangingEmployeeNumbersInput,
): string {
  const filename = safe(input.filename, 'ExcelFileName.xlsx');
  const sheetName = safe(input.sheetName, 'Sheet 1');
  return `wb := Xlsx.Workbook filename: '${filename}'.
sh := wb sheetNamed: '${sheetName}'.
notFound := Core.OrderedCollection new.
sh rowsWithoutHeaderRowDo:
        [:row |
            oldNumber := row trimmedValueAt: 1.
            newNumber := row trimmedValueAt: 2.
            emp := repository empNumbered: oldNumber.
            emp isNil
                ifTrue: [notFound add: oldNumber]
                ifFalse: [emp employeeNumber: newNumber asString.]
        ].
notFound.`;
}

export function generateRemoveNonDefaultGroupingTypeLanguages(): string {
  return `"Audit — how many languages populate each grouping type name."
report := repository groupingTypes collect:
        [:gt | | iname localeKeys filledLangs |
            iname := gt name.
            iname isIString
                ifTrue:
                        [localeKeys := iname localeKeys asArray.
                        filledLangs := localeKeys
                                select: [:key | (iname valueAt: key asString) notEmpty].
                        SMap
                                code: gt code
                                isIString: true
                                storedLangCount: localeKeys size
                                filledLangCount: filledLangs size
                                filledLangs: filledLangs asArray]
                ifFalse:
                        [SMap
                                code: gt code
                                isIString: false
                                storedLangCount: 0
                                filledLangCount: (iname isNilOrEmpty ifTrue: [0] ifFalse: [1])
                                filledLangs: #()]].
sortedReport := report asSortedCollection: [:a :b | a filledLangCount <= b filledLangCount].

"Reduce — collapse each grouping type name to only its en_us translation."
skipped := Core.OrderedCollection new.
repository groupingTypes do:
        [:gt | | iname enValue |
            iname := gt name.
            enValue := iname isIString
                ifTrue: [iname asEnglishString]
                ifFalse: [iname].
            (enValue isNil or: [enValue isEmpty])
                ifTrue: [skipped add: gt code]
                ifFalse:
                        [gt name:
                                (Dragoman.IString value:
                                        (Core.StringMap new
                                                jsAt: 'en_us' put: enValue;
                                                yourself))]].

SMap audit: sortedReport skipped: skipped.`;
}
